/**
 * The witness's only state, and the three channels it listens on.
 *
 * MODULE SCOPED, NOT COMPONENT SCOPED. Chrome dispatches `toolactivated`
 * synchronously at `window` the instant a tool is activated, which can be
 * before React has drawn anything and can be while the overlay is unmounted by
 * a route change. State that lived in a component would lose those, so the
 * journal outlives every render and React reads it through
 * `useSyncExternalStore`.
 *
 * THE THREE CHANNELS.
 *
 *  1. `toolactivated` and `toolcancel` on `window`. Chrome's own, undocumented,
 *     synchronous and non-cancelable, and they carry a tool name and nothing
 *     else. They are enough to open a row and to cancel one, and never enough
 *     to show a diff.
 *  2. `webmcp:witness`, this module's own channel. Anything that knows more
 *     than a tool name publishes here: the operator panel publishes both ends
 *     of its own invocations, and the runtime can publish a settled diff in a
 *     one-line change without this module knowing it exists.
 *  3. `tool-completion-${requestId}`, the handshake completion event from
 *     `handshake.ts`. Subscribed per invocation, and only for an invocation
 *     that published a `requestId`, because the event name is derived from that
 *     id and there is no way to listen for it before one exists.
 */
import type { Consequence, Diff } from '../contract';
import {
  openRow,
  settleRow,
  type WitnessRow,
  type WitnessSource,
} from './journal';
import { consequenceFor, verbSentence } from './sentence';

export const WITNESS_CHANNEL = 'webmcp:witness';

export type WitnessSignal =
  | {
      readonly kind: 'started';
      readonly id: string;
      readonly toolName: string;
      readonly source?: WitnessSource;
      readonly sentence?: string;
      readonly consequence?: Consequence | null;
      readonly diff?: Diff | null;
      /** Correlates with `handshake.dispatchAndWait`, which mints one per call. */
      readonly requestId?: string;
    }
  | {
      readonly kind: 'settled';
      readonly id?: string;
      readonly toolName?: string;
      readonly outcome: 'ok' | 'refused' | 'cancelled';
      readonly diff?: Diff | null;
      readonly refusal?: string | null;
    };

/**
 * How long a `started` published by a human invocation suppresses the
 * `toolactivated` Chrome fires for the same call.
 *
 * The panel publishes before it calls `executeTool` precisely so the two arrive
 * in that order, and Chrome dispatches synchronously inside the call, so the
 * real gap is microseconds. The window is generous because the failure it
 * prevents is a duplicate card and the failure it could cause, swallowing a
 * genuine second invocation of the same tool, needs an operator to press
 * Invoke twice inside it.
 */
const CLAIM_WINDOW_MS = 2000;

let rows: readonly WitnessRow[] = [];
const listeners = new Set<() => void>();
let installed = false;

function emit(): void {
  for (const listener of listeners) listener();
}

function setRows(next: readonly WitnessRow[]): void {
  if (next === rows) return;
  rows = next;
  emit();
}

function newId(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function hasFreshHumanClaim(toolName: string, now: number): boolean {
  return rows.some(
    (row) =>
      row.toolName === toolName &&
      row.source === 'human' &&
      row.outcome === 'pending' &&
      now - row.startedAt < CLAIM_WINDOW_MS,
  );
}

function onToolActivated(event: Event): void {
  const toolName = (event as WebMCPEvent).toolName;
  if (typeof toolName !== 'string' || toolName.length === 0) return;

  const now = Date.now();
  if (hasFreshHumanClaim(toolName, now)) return;

  setRows(
    openRow(rows, {
      id: newId(),
      toolName,
      sentence: verbSentence(toolName),
      consequence: consequenceFor(toolName),
      source: 'agent',
      startedAt: now,
    }),
  );
}

function onToolCancel(event: Event): void {
  const toolName = (event as WebMCPEvent).toolName;
  if (typeof toolName !== 'string' || toolName.length === 0) return;
  setRows(settleRow(rows, { toolName }, { outcome: 'cancelled', settledAt: Date.now() }));
}

/**
 * Waits for the view to confirm, then settles the row as committed.
 *
 * Only ever upgrades a row that is still pending. A `settled` signal on the
 * witness channel is authoritative and arrives first when the caller has one,
 * so a late handshake must not overwrite a refusal with a success.
 */
function watchHandshake(id: string, requestId: string): void {
  const name = `tool-completion-${requestId}`;
  const onCompletion = (): void => {
    window.removeEventListener(name, onCompletion);
    const pending = rows.find((row) => row.id === id);
    if (!pending || pending.outcome !== 'pending') return;
    setRows(settleRow(rows, { id }, { outcome: 'ok', settledAt: Date.now() }));
  };
  window.addEventListener(name, onCompletion);
}

function onWitnessSignal(event: Event): void {
  const detail = (event as CustomEvent<unknown>).detail;
  if (typeof detail !== 'object' || detail === null) return;
  const signal = detail as WitnessSignal;

  if (signal.kind === 'started') {
    if (typeof signal.toolName !== 'string' || signal.toolName.length === 0) return;
    setRows(
      openRow(rows, {
        id: signal.id,
        toolName: signal.toolName,
        sentence: signal.sentence ?? verbSentence(signal.toolName),
        consequence: signal.consequence ?? consequenceFor(signal.toolName),
        source: signal.source ?? 'agent',
        startedAt: Date.now(),
        diff: signal.diff ?? null,
      }),
    );
    if (typeof signal.requestId === 'string' && signal.requestId.length > 0) {
      watchHandshake(signal.id, signal.requestId);
    }
    return;
  }

  if (signal.kind === 'settled') {
    const match = signal.id !== undefined ? { id: signal.id } : { toolName: signal.toolName };
    setRows(
      settleRow(rows, match, {
        outcome: signal.outcome,
        diff: signal.diff ?? null,
        refusal: signal.refusal ?? null,
        settledAt: Date.now(),
      }),
    );
  }
}

/** Idempotent, so an HMR re-evaluation cannot double-count an invocation. */
export function installWitnessListeners(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('toolactivated', onToolActivated);
  window.addEventListener('toolcancel', onToolCancel);
  window.addEventListener(WITNESS_CHANNEL, onWitnessSignal);
}

installWitnessListeners();

export function subscribeWitness(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function witnessSnapshot(): readonly WitnessRow[] {
  return rows;
}

/** Publishes a start. Returns the id the matching settle must cite. */
export function publishWitnessStart(
  signal: Omit<Extract<WitnessSignal, { kind: 'started' }>, 'kind' | 'id'> & { id?: string },
): string {
  const id = signal.id ?? newId();
  window.dispatchEvent(
    new CustomEvent(WITNESS_CHANNEL, { detail: { ...signal, kind: 'started', id } }),
  );
  return id;
}

export function publishWitnessSettle(
  signal: Omit<Extract<WitnessSignal, { kind: 'settled' }>, 'kind'>,
): void {
  window.dispatchEvent(
    new CustomEvent(WITNESS_CHANNEL, { detail: { ...signal, kind: 'settled' } }),
  );
}

/** Test seam. Production code has no reason to clear a record of what happened. */
export function resetWitnessForTests(): void {
  rows = [];
  emit();
}
